import React, { lazy, Suspense, useEffect, useRef } from "react";
import {
  Box,
  Center,
  Flex,
  Text,
  Button,
  VStack,
  Image,
  Container,
  Spinner,
  Wrap,
  WrapItem,
  Icon,
  keyframes,
  usePrefersReducedMotion,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaMapMarkerAlt, FaRegClock, FaHeart } from "react-icons/fa";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import SiteFooter from "./SiteFooter";
import FloatingPaint from "./components/FloatingPaint";
import DeferredRender from "./components/DeferredRender";
import { FESTIVAL } from "./data/festival";
import Header from "./Header";
const HomeBelowFold = lazy(() => import("./HomeBelowFold"));
const WorkWithUs = lazy(() => import("./WorkWithUs"));
const NotFoundPage = lazy(() => import("./NotFoundPage"));
const AboutPage = lazy(() => import("./AboutPage"));
const EntertainmentPage = lazy(() => import("./EntertainmentPage"));
const GiveawayPage = lazy(() => import("./GiveawayPage"));
const InternationalZonePage = lazy(() => import("./InternationalZonePage"));

const RouteLoadingState = () => (
  <Center minH="100vh" pt="96px" bg="white">
    <Spinner
      thickness="4px"
      speed="0.7s"
      emptyColor="blue.100"
      color="#0C7FBC"
      size="xl"
      label="Loading page"
    />
  </Center>
);

const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const samePage = lastPath.current === pathname;
    lastPath.current = pathname;
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return undefined;
    }
    // In-page #links glide; arriving from elsewhere jumps straight there.
    const behavior = samePage ? "smooth" : "auto";
    const id = decodeURIComponent(hash.slice(1));
    const timers = [];
    let cancelled = false;
    let tries = 0;
    // Never fight a visitor who starts scrolling on their own.
    const stop = () => {
      cancelled = true;
    };
    USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { passive: true }));

    const align = (how) => {
      const target = document.getElementById(id);
      if (target && !cancelled) target.scrollIntoView({ behavior: how, block: "start" });
    };
    // Lazy pages mount a moment after navigation: wait for the target.
    const findTarget = () => {
      if (cancelled) return;
      if (document.getElementById(id)) {
        align(behavior);
        // Images and opening panels above it can push the target down; re-align as they settle.
        if (behavior === "auto") {
          [300, 800, 1500, 2500].forEach((ms) => timers.push(setTimeout(() => align("auto"), ms)));
        }
      } else if (tries++ < 120) {
        timers.push(setTimeout(findTarget, 50));
      }
    };
    findTarget();

    return () => {
      timers.forEach(clearTimeout);
      USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop));
    };
  }, [pathname, hash]);

  return null;
};

const PAGE_TITLES = {
  "/": "8th Annual Culver City Arts Festival · Saturday, October 10, 2026",
  "/about": "About & Getting Here · Culver City Arts Festival",
  "/entertainment": "Lineup & Set Times · Culver City Arts Festival",
  "/map": "Festival Map · Culver City Arts Festival",
  "/giveaway": "Festival Map · Culver City Arts Festival",
  "/international-zone": "International Arts Zone · Culver City Arts Festival",
  "/work-with-us": "Vendors & Volunteers · Culver City Arts Festival",
};

// Per-page <title> and canonical link.
const RouteMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname === "/giveaway" ? "/map" : pathname;
    document.title = PAGE_TITLES[pathname] || "Page not found · Culver City Arts Festival";
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://culvercityartsfestival.com${path === "/" ? "/" : path}`);
  }, [pathname]);

  return null;
};

const heroFacts = [
  { icon: FaHeart, label: "FREE for all ages", color: "#D73522" },
  { icon: FaMapMarkerAlt, label: "Washington Blvd · Arts District", color: "#F47A22" },
  { icon: FaRegClock, label: FESTIVAL.hoursLabel, color: "#0C7FBC" },
];



const heroPan = keyframes`
  0%   { transform: scale(1.04) translate3d(0, 0, 0); }
  100% { transform: scale(1.12) translate3d(-1.5%, -1.2%, 0); }
`;

const HeroSection = ({ handleStartJourney, buttonSize }) => {
  const reduceMotion = usePrefersReducedMotion();

  return (
    <Box
      as="section"
      position="relative"
      minH="100vh"
      height="auto"
      width="100%"
      overflow="hidden"
      overflowX="hidden"
      bg="white"
    >
      {/* Poster-style paint sweep background */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        backgroundImage="url('/brand/arts-festival-hero-bg.webp')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        // Ambient motion starts once the page has loaded.
        animation={reduceMotion ? undefined : `${heroPan} 26s ease-in-out 4s infinite alternate`}
      />

      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        bg="linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.48) 42%, rgba(255,255,255,0.92) 100%)"
      />

      <FloatingPaint startDelay={4} />

      {/* Content */}
      <Container
        maxW="container.xl"
        minH="inherit"
        position="relative"
        zIndex="3"
        px={{ base: 4, md: 6, lg: 8 }}
      >
        <VStack
          spacing={{ base: 3, sm: 4, md: 5, lg: 6 }}
          align="center"
          justify="center"
          minH="inherit"
          width="100%"
          maxW="100%"
          color="#063F74"
          pt={{ base: "88px", md: "102px", lg: "108px" }}
          pb={{ base: "32px", md: "40px", lg: "44px" }}
        >
          <Box
            width="100%"
            maxW={{ base: "100%", sm: "560px", lg: "620px" }}
            aspectRatio="3 / 2"
            display="flex"
            justifyContent="center"
          >
            <Image
              src="/brand/arts-festival-logo.webp"
              alt="8th Annual Culver City Arts Festival"
              width="100%"
              maxWidth="100%"
              height="auto"
              margin="0 auto"
              mb={[0, 1, 1]}
              as={motion.img}
              initial={reduceMotion ? false : { scale: 0.9, rotate: -3 }}
              animate={{ scale: 1, rotate: 0 }}
              whileHover={{ scale: 1.05, rotate: -1 }}
              transition={{ type: "spring", stiffness: 180, damping: 14 }}
              filter="drop-shadow(0 18px 34px rgba(4, 48, 92, 0.28))"
              loading="eager"
              decoding="async"
            />
          </Box>

          <Box
            width="100%"
            display="flex"
            justifyContent="center"
          >
            <Text
              fontSize={{ base: "xl", sm: "2xl", md: "4xl", lg: "4xl" }}
              fontWeight="900"
              textAlign="center"
              textShadow="0 3px 0 rgba(255,255,255,0.75)"
              color="#063F74"
              mb={[2, 3, 3]}
              letterSpacing={{ base: "0", md: "0.04em" }}
              fontFamily="'Poppins', sans-serif"
              lineHeight={["1.1", "1.1", "1.1"]}
              width={{ base: "100%", md: "auto" }}
              maxW="100%"
              px={{ base: 3, sm: 4, md: 8 }}
              py={[2, 2, 3]}
              bg="rgba(255,255,255,0.82)"
              border="3px solid #0C7FBC"
              borderRadius="16px"
              boxShadow="0 10px 30px rgba(12, 127, 188, 0.18)"
            >
              Saturday, October 10, 2026
            </Text>
          </Box>

          <Box
            width="100%"
            display="flex"
            justifyContent="center"
          >
            <Text
              fontSize={{ base: "sm", sm: "md", md: "lg", lg: "xl" }}
              maxW="2xl"
              width="100%"
              textAlign="center"
              color="#063F74"
              bg="rgba(255,255,255,0.86)"
              px={{ base: 4, md: 6, lg: 8 }}
              py={{ base: 3, md: 4, lg: 5 }}
              borderRadius="18px"
              border="2px solid rgba(244, 122, 34, 0.5)"
              boxShadow="0 12px 30px rgba(244, 122, 34, 0.16)"
              fontWeight="700"
              letterSpacing="0"
            >
              Now in its eighth year, this FREE Festival brings the streets to life with live art-making, artisan vendors, nonstop music, food trucks, beer gardens, and fun for all ages!
            </Text>
          </Box>

          <Wrap justify="center" spacing={{ base: 2, md: 3 }}>
            {heroFacts.map((fact) => (
              <WrapItem key={fact.label}>
                <Flex
                  align="center"
                  gap={2}
                  bg="rgba(255,255,255,0.9)"
                  border={`2px solid ${fact.color}`}
                  borderRadius="full"
                  px={{ base: 3, md: 4 }}
                  py={{ base: 1.5, md: 2 }}
                  color="#063F74"
                  fontWeight="800"
                  fontSize={{ base: "sm", md: "md" }}
                  boxShadow="0 8px 20px rgba(6,63,116,0.1)"
                >
                  <Icon as={fact.icon} color={fact.color} aria-hidden="true" />
                  {fact.label}
                </Flex>
              </WrapItem>
            ))}
          </Wrap>

          <Wrap justify="center" spacing={{ base: 3, md: 4 }}>
            <WrapItem>
            <Button
              as="a"
              href="#lineup"
              size={buttonSize}
              bg="#0C7FBC"
              color="white"
              fontFamily="'Poppins', sans-serif"
              fontWeight="900"
              px={10}
              py={6}
              fontSize="xl"
              border="3px solid #0C7FBC"
              borderRadius="14px"
              boxShadow="0 12px 32px rgba(12, 127, 188, 0.3)"
              letterSpacing="0.04em"
              _hover={{
                transform: "translateY(-5px) scale(1.05)",
                boxShadow: "0 15px 40px rgba(12, 127, 188, 0.4)",
                bg: "#063F74",
                borderColor: "#063F74",
              }}
              _active={{
                transform: "translateY(-2px) scale(1.02)"
              }}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            >
              See the Lineup
            </Button>
            </WrapItem>
            <WrapItem>
            <Button
              size={buttonSize}
              bg="white"
              color="#0C7FBC"
              fontFamily="'Poppins', sans-serif"
              fontWeight="900"
              px={10}
              py={6}
              fontSize="xl"
              border="3px solid #F47A22"
              borderRadius="14px"
              boxShadow="0 12px 32px rgba(244, 122, 34, 0.28)"
              textShadow="none"
              letterSpacing="0.04em"
              _hover={{ 
                transform: "translateY(-5px) scale(1.05)", 
                boxShadow: "0 15px 40px rgba(244, 122, 34, 0.36)",
                bg: "#F47A22",
                color: "white"
              }}
              _active={{
                transform: "translateY(-2px) scale(1.02)"
              }}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              onClick={handleStartJourney}
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: "linear-gradient(90deg, transparent, rgba(255,213,46,0.35), transparent)",
                transition: "left 0.5s"
              }}
              _groupHover={{
                _before: {
                  left: "100%"
                }
              }}
            >
              Join Us
            </Button>
            </WrapItem>
          </Wrap>
        </VStack>
      </Container>
    </Box>
  );
};

// Floating Action Button Component
// const FloatingActionButton = () => {
//   const navigate = useNavigate();
//   
//   return (
//     <Box
//       position="fixed"
//       bottom="30px"
//       right="30px"
//       zIndex="1000"
//       as={motion.div}
//       initial={{ scale: 0, opacity: 0 }}
//       animate={{ scale: 1, opacity: 1 }}
//       transition={{ delay: 2, type: "spring", stiffness: 300 }}
//     >
//              <Button
//         borderRadius="full"
//         size="lg"
//         bg="linear-gradient(135deg, #0C7FBC 0%, #F47A22 100%)"
//         color="white"
//         fontFamily="'Shrikhand', 'Poppins', sans-serif"
//         fontWeight="normal"
//         fontSize="lg"
//         letterSpacing="wide"
//         textShadow="2px 2px 4px rgba(0,0,0,0.3)"
//         boxShadow="0 8px 25px rgba(12, 127, 188, 0.4)"
//         _hover={{
//           transform: "scale(1.1)",
//           boxShadow: "0 12px 35px rgba(12, 127, 188, 0.6)"
//         }}
//         _active={{ transform: "scale(0.95)" }}
//         transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
//         onClick={() => navigate("/work-with-us")}
//         position="relative"
//         _before={{
//           content: '""',
//           position: "absolute",
//           top: "-5px",
//           left: "-5px",
//           right: "-5px",
//           bottom: "-5px",
//           background: "linear-gradient(135deg, #0C7FBC, #F47A22)",
//           borderRadius: "full",
//           zIndex: -1,
//           animation: "pulse 2s infinite"
//         }}
//       >
//         🎨 Join Us
//       </Button>
//      <style>
//        {`
//          @keyframes pulse {
//            0% { transform: scale(1); opacity: 1; }
//            50% { transform: scale(1.05); opacity: 0.7; }
//            100% { transform: scale(1); opacity: 1; }
//          }
//        `}
//      </style>
//    </Box>
//  );
// };

// Main App component
const App = () => {
  const buttonSize = useBreakpointValue({ base: "md", md: "lg" });
  const navigate = useNavigate();

  const handleStartJourney = () => {
    navigate("/work-with-us");
  };

  return (
    <Box minHeight="100vh">
      <ScrollToTop />
      <RouteMeta />
      <Header />
      <Suspense fallback={<RouteLoadingState />}>
        <Routes>
          <Route
            path="/"
            element={
              <Box as="main">
              <HeroSection
                handleStartJourney={handleStartJourney}
                buttonSize={buttonSize}
              />

              <Suspense fallback={null}>
                <DeferredRender>
                  <HomeBelowFold />
                </DeferredRender>
              </Suspense>
              </Box>
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/work-with-us" element={<WorkWithUs />} />
          <Route path="/entertainment" element={<EntertainmentPage />} />
          <Route path="/map" element={<GiveawayPage />} />
          <Route path="/giveaway" element={<GiveawayPage />} />
          <Route path="/international-zone" element={<InternationalZonePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      <SiteFooter />

      {/* <FloatingActionButton /> */}

    </Box>
  );
};

export default App;
